import { useState, useEffect } from 'react';
import WinningSoundsReveal from '../Common/WinningSoundsReveal.jsx';
import useAudioEngine from '../../audio/useAudioEngine.js';
import '../../styles/theme.css';

export default function TestStudio() {
  const [roomCode, setRoomCode] = useState('');
  const [playerCount, setPlayerCount] = useState(4);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [showTeamRevealTest, setShowTeamRevealTest] = useState(false);
  const studioAudio = useAudioEngine('host');

  useEffect(() => {
    // Generate a fresh room code by hitting the server endpoint so we get a
    // genuine DO allocation rather than falling back to a hardcoded code.
    fetch('/api/room/new')
      .then((r) => r.json())
      .then((data) => {
        if (data?.roomCode) setRoomCode(data.roomCode);
      })
      .catch(() => {
        // Fallback: four uppercase letters, but still random per harness session.
        const code = Math.random().toString(36).substring(2, 6).toUpperCase();
        setRoomCode(code);
      });
  }, []);

  const players = ['Player_1', 'Player_2', 'Player_3', 'Player_4', 'Player_5', 'Player_6', 'Player_7', 'Player_8'].slice(0, playerCount);

  const handleEnableAudio = () => {
    setAudioUnlocked(true);
    // Post message to host iframe to trigger audio unlock
    const hostFrame = document.getElementById('host-iframe');
    if (hostFrame && hostFrame.contentWindow) {
      hostFrame.contentWindow.postMessage({ type: 'UNLOCK_AUDIO' }, '*');
    }
    // Also post to player iframes
    players.forEach((_, idx) => {
      const playerFrame = document.getElementById(`player-iframe-${idx}`);
      if (playerFrame && playerFrame.contentWindow) {
        playerFrame.contentWindow.postMessage({ type: 'UNLOCK_AUDIO' }, '*');
      }
    });
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      background: 'var(--sd-ground, #08090B)',
      color: 'var(--sd-ink, #EDEFF2)',
      padding: '12px',
      boxSizing: 'border-box',
      fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
    }}>
      <header style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        paddingBottom: '12px',
        borderBottom: '1px solid var(--sd-line, #1A1F25)',
        flexWrap: 'wrap',
      }}>
        <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>🎧 SoundDrift Test Studio</h1>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--sd-dim, #6E7982)' }}>
          Room Code:
          <input
            type="text"
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
            style={{
              padding: '6px 10px',
              borderRadius: '8px',
              background: 'var(--sd-panel-2, #0E1116)',
              color: 'var(--sd-ink, #EDEFF2)',
              border: '1px solid var(--sd-line-2, #1C2229)',
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontWeight: 700,
            }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--sd-dim, #6E7982)' }}>
          Players:
          <select
            value={playerCount}
            onChange={(e) => setPlayerCount(Number(e.target.value))}
            style={{
              padding: '6px 10px',
              borderRadius: '8px',
              background: 'var(--sd-panel-2, #0E1116)',
              color: 'var(--sd-ink, #EDEFF2)',
              border: '1px solid var(--sd-line-2, #1C2229)',
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            }}
          >
            {[2, 3, 4, 6, 8].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <button
          type="button"
          data-testid="studio-restart-room-btn"
          onClick={() => {
            fetch('/api/room/new')
              .then((r) => r.json())
              .then((data) => {
                if (data?.roomCode) setRoomCode(data.roomCode);
              })
              .catch(() => {
                const code = Math.random().toString(36).substring(2, 6).toUpperCase();
                setRoomCode(code);
              });
          }}
          style={{
            padding: '8px 14px',
            borderRadius: '10px',
            background: 'var(--sd-panel-2, #0E1116)',
            color: 'var(--sd-ink, #EDEFF2)',
            border: '1px solid var(--sd-line, #1A1F25)',
            fontWeight: 600,
            fontSize: '13px',
            cursor: 'pointer',
            minHeight: '44px',
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          🔄 Restart New Room
        </button>
        <button
          type="button"
          data-testid="test-winning-reveal-trigger"
          onClick={() => {
            setShowTeamRevealTest(!showTeamRevealTest);
            if (!audioUnlocked) handleEnableAudio();
          }}
          style={{
            padding: '8px 14px',
            borderRadius: '10px',
            background: showTeamRevealTest ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-panel-2, #0E1116)',
            color: showTeamRevealTest ? '#08090B' : 'var(--sd-teal, #3ACFCF)',
            border: '1px solid var(--sd-teal, #3ACFCF)',
            fontWeight: 700,
            fontSize: '13px',
            cursor: 'pointer',
            minHeight: '44px',
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          🎉 Test Team Reveal Audio
        </button>
        <button
          onClick={handleEnableAudio}
          style={{
            padding: '8px 18px',
            borderRadius: '10px',
            background: audioUnlocked ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-amber, #E0A458)',
            color: 'var(--sd-ground, #08090B)',
            border: 'none',
            fontWeight: 800,
            fontSize: '13px',
            cursor: 'pointer',
            marginLeft: 'auto',
            minHeight: '44px',
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
          }}
        >
          {audioUnlocked ? '🔊 Studio Audio Enabled' : '🔇 Enable Studio Audio (Click to Unlock)'}
        </button>
      </header>

      {showTeamRevealTest && (
        <div style={{ marginBottom: '12px' }}>
          <WinningSoundsReveal
            audioEngine={studioAudio}
            title="🎧 Test Studio Preview: Winning Sounds Merged"
          />
        </div>
      )}

      {!roomCode ? (
        <div data-testid="allocating-room" style={{ padding: '24px', color: 'var(--sd-dim, #6E7982)' }}>
          Allocating room code…
        </div>
      ) : (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', flex: 1, marginTop: '12px', minHeight: 0 }}>
        {/* Host Frame */}
        <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--sd-line, #1A1F25)', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ background: 'var(--sd-panel-2, #0E1116)', padding: '8px 14px', fontSize: '13px', fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--sd-line, #1A1F25)' }}>
            <span>📺 Host Screen</span>
            {!audioUnlocked && (
              <span style={{ fontSize: '11px', color: 'var(--sd-amber, #E0A458)', fontWeight: 'normal', fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace" }}>
                Tap "Sound Muted" inside or click "Enable Studio Audio" above
              </span>
            )}
          </div>
          <iframe
            id="host-iframe"
            title="Host Screen"
            src={`/host?roomCode=${roomCode}&isHost=true`}
            allow="autoplay; audio; microphone"
            style={{ width: '100%', height: '100%', border: 'none', background: 'var(--sd-ground, #08090B)' }}
          />
        </div>

        {/* Players Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', overflowY: 'auto' }}>
          {players.map((name, idx) => (
            <div key={name} style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--sd-line-2, #1C2229)', borderRadius: '12px', overflow: 'hidden', minHeight: '440px' }}>
              <div style={{ background: 'var(--sd-panel-2, #0E1116)', padding: '6px 12px', fontSize: '12px', fontWeight: 700, borderBottom: '1px solid var(--sd-line, #1A1F25)' }}>
                📱 {name}
              </div>
              <iframe
                id={`player-iframe-${idx}`}
                title={name}
                src={`/?roomCode=${roomCode}&playerName=${encodeURIComponent(name)}&debug_name=${encodeURIComponent(name)}`}
                allow="autoplay; audio; microphone"
                style={{ width: '100%', height: '100%', border: 'none', background: 'var(--sd-ground, #08090B)' }}
              />
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}
